import React, { useState, useEffect } from "react";
import { Drawer, Table, TableColumnsType, Empty, Spin, Flex } from "antd";
import mainDashboardApis from "../../api/dashboard/MainDashboard";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import statusMapper from "../payments/StatusMapper";
import { useSelector } from "react-redux";

interface InvoicesDrawerProps {
  visible: boolean;
  onClose: () => void;
  site: string;
  contractor: string;
  dateRange: { from: string; to: string };
  categoryId?: string;
}

const InvoicesDrawer: React.FC<InvoicesDrawerProps> = ({
  visible,
  onClose,
  site,
  contractor,
  dateRange,
  categoryId,
}) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [allData, setAllData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  const columns: TableColumnsType<any> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceId",
      key: "invoiceId",
      width: 200,
      fixed: "left",
      render: (text: any, record: any) => {
        if (!text && !record.invoiceNumber) return <span>-</span>;
        const invoiceId = text || record.invoiceId;
        const invoiceNumber = record.invoiceNumber;
        return (
          <div className="align-left">
            <span
              className="inlineTableCellLink"
              style={{ cursor: "pointer", color: "#2263b3" }}
              onClick={() => {
                if (invoiceNumber) {
                  navigate(`/view-invoice/${invoiceNumber}`, {
                    state: {
                      fromInvoicesDrawer: true,
                      previousPath: "/dashboard",
                      previousState: {
                        openInvoicesDrawer: true,
                        site,
                        contractor,
                        dateRange,
                        categoryId,
                      },
                    },
                  });
                }
              }}
            >
              {invoiceId}
            </span>
          </div>
        );
      },
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 150,
      render: (date: any) =>
        date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Supplier",
      dataIndex: "Supplier",
      key: "Supplier",
      width: 200,
    },
    {
      title: "Site ID",
      dataIndex: "siteId",
      key: "siteId",
      width: 120,
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      width: 150,
      render: (text: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Order Number",
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 180,
      fixed: "right",
      render: (status: any) => {
        const _status = statusMapper[status?.toLowerCase()]?.seller;
        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color: `${_status?.color || "#000"}`,
                fontSize: "14px",
              }}
            >
              {_status?.icon} {_status?.title || status}
            </h6>
          </div>
        );
      },
    },
    {
      title: "Payment Status",
      dataIndex: "actualStatus",
      key: "actualStatus",
      fixed: "right",
      width: 150,
      align: "left",
      render: (text, record) => {
        const status = text || record.paymentStatus;
        if (!status) return <span>-</span>;
        return (
          <>
            <div className="justify-start align-center">
              <h6
                className={`tb-status ms-1`}
                style={{
                  fontSize: "14px",
                  color:
                    status === "Approved"
                      ? "#28A745" // Green
                      : status === "Open"
                      ? "#007BFF" // Blue
                      : status === "In Progress"
                      ? "#FD7E14" // Orange
                      : status === "Rejected"
                      ? "#DC3545" // Red
                      : status === "Pending"
                      ? "#FFC107" // Yellow
                      : "black", // Default color
                }}
              >
                {status}
              </h6>
            </div>
          </>
        );
      },
    },
    {
      title: "Delivery Days",
      dataIndex: "DeliveryDays",
      key: "DeliveryDays",
      width: 160,
      render: (text: any, record: any) => {
        if (!text) return <span>-</span>;
        
        // Parse the DeliveryDays string (e.g., "overDue by 112 Days")
        const isOverdue = text.toLowerCase().includes("overdue");
        const isHighlighted = isOverdue;
        
        // Extract the number of days if available
        const daysMatch = text.match(/\d+/);
        const days = daysMatch ? parseInt(daysMatch[0]) : 0;
        
        let placeholder = "";
        if (isOverdue) {
          placeholder = "OVERDUE BY " + Math.abs(days) + " DAYS";
        } else {
          if (days == 0) {
            placeholder = "DUE TODAY";
          } else {
            placeholder = "DUE IN " + Math.abs(days) + " DAYS";
          }
        }
        
        return (
          <div
            className="align-left"
            style={{
              color: isHighlighted ? "lightcoral" : "green",
              backgroundColor: isHighlighted ? "white" : "inherit",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            {placeholder}
          </div>
        );
      },
    },
  ];

  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      const requestData: any = {
        startDate: dateRange.from,
        endDate: dateRange.to,
      };

      if (site) {
        requestData.siteId = site;
      }

      if (currentUser?.orgid) {
        requestData.orgId = currentUser.orgid;
      }

      if (categoryId) {
        requestData.categoryId = categoryId;
      }

      if (contractor) {
        requestData.contractorId = contractor;
      }

      const res = await mainDashboardApis.invoiceDetails(requestData);
      if (res?.status) {
        // Handle response - adjust based on actual API response structure
        const responseData = res.response?.data || res.response || [];
        setAllData(responseData);
        setPagination({
          current: 1,
          pageSize: 10,
          total: responseData.length || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && dateRange.from && dateRange.to) {
      fetchInvoices();
    }
  }, [visible, site, contractor, dateRange, categoryId]);

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: allData.length,
    });
  };

  // Calculate paginated data for display
  const paginatedData = allData.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  return (
    <Drawer
      title="Invoices List"
      placement="right"
      onClose={onClose}
      open={visible}
      width="85%"
    >
      {isLoading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin size="large" className="m-auto" />
        </Flex>
      ) : allData.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Empty description="No Invoices Found" />
        </div>
      ) : (
        <Table
          className="customTable"
          columns={columns}
          dataSource={paginatedData}
          rowKey={(record) => record.invoiceNumber || record.invoiceId}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
          onChange={handleTableChange}
          bordered
          size="middle"
          scroll={{ x: 1200 }}
        />
      )}
    </Drawer>
  );
};

export default InvoicesDrawer;

